import { Box, MenuItem, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
  CustomButton,
  CustomSelect,
  CustomTextField,
  HorizontalForm,
  VerticalForm,
} from '../../../common/components';
import {
  GERENCIALES_DATE_RANGE_REPORTS,
  GERENCIALES_DEBT_STATUS_REPORT_TYPE,
  GERENCIALES_DEBT_STATUS_POSSESSOR_OPTIONS,
  GERENCIALES_DEBT_STATUS_ORDER_OPTIONS,
  GERENCIALES_DEBT_STATUS_VIEW_OPTIONS,
  GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE,
  GERENCIALES_APP_USAGE_OPTIONS,
  GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE,
} from '../constants/gerencialesConstants';

/**
 * Filtros para el modulo de estadisticas gerenciales.
 * @param {Object} props - Props del componente.
 * @param {string} props.reportType - Tipo de reporte seleccionado.
 * @param {Array<{label: string, value: string}>} props.reportOptions - Opciones del selector.
 * @param {boolean} props.loading - Estado de carga.
 * @param {string|null} props.error - Mensaje de error.
 * @param {Object|null} props.header - Header de respuesta.
 * @param {string} props.dateStart - Fecha desde.
 * @param {string} props.dateEnd - Fecha hasta.
 * @param {Function} props.onDateChange - Handler de cambio de fechas.
 * @param {Function} props.onReportTypeChange - Handler de cambio de reporte.
 * @param {Function} props.onReload - Handler de recarga.
 * @param {Function} props.onDownloadPdf - Handler de descarga PDF.
 * @param {boolean} props.pdfCooldown - Bloquea descarga mientras se renderiza el grafico.
 * @param {boolean} props.disableDownload - Deshabilita descarga cuando no hay data.
 * @param {Function} props.onDownloadXlsx - Handler de descarga XLSX.
 * @param {boolean} props.disableDownloadXlsx - Deshabilita descarga XLSX.
 * @param {boolean} props.showDownloadXlsx - Muestra botón XLSX.
 * @param {Object} props.debtStatusFilters - Filtros del reporte de mora.
 * @param {Function} props.onDebtStatusFilterChange - Handler de filtros de mora.
 * @param {Object} props.appSubscribersFilters - Filtros del reporte de app.
 * @param {Function} props.onAppSubscribersFilterChange - Handler de filtros de app.
 * @param {Object} props.archivedContractsFilters - Filtros del reporte de bajas.
 * @param {Function} props.onArchivedContractsFilterChange - Handler de filtros de bajas.
 * @param {string} props.reportView - Vista seleccionada en reportes con tabs.
 * @param {Function} props.onReportViewChange - Handler de cambio de vista.
 * @param {Array<Object>} props.paymentMethods - Formas de pago disponibles.
 * @param {boolean} props.paymentMethodsLoading - Estado de carga de formas de pago.
 * @param {Array<Object>} props.releaseReasons - Motivos de baja disponibles.
 * @param {boolean} props.releaseReasonsLoading - Estado de carga de motivos de baja.
 * @returns {JSX.Element} Barra de filtros.
 */
const GerencialesFilters = ({
  reportType,
  reportOptions,
  loading,
  error,
  header,
  dateStart,
  dateEnd,
  onDateChange,
  onReportTypeChange,
  onReload,
  onDownloadPdf,
  pdfCooldown,
  disableDownload,
  onDownloadXlsx,
  disableDownloadXlsx,
  showDownloadXlsx,
  debtStatusFilters,
  onDebtStatusFilterChange,
  appSubscribersFilters,
  onAppSubscribersFilterChange,
  archivedContractsFilters,
  onArchivedContractsFilterChange,
  reportView,
  onReportViewChange,
  paymentMethods,
  paymentMethodsLoading,
  releaseReasons,
  releaseReasonsLoading,
}) => {
  const showDateFilters =
    GERENCIALES_DATE_RANGE_REPORTS.includes(reportType) ||
    reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE;
  const showDebtStatusFilters = reportType === GERENCIALES_DEBT_STATUS_REPORT_TYPE;
  const showAppSubscribersFilters = reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE;
  const showArchivedContractsFilters = reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE;
  const showReportView =
    showDebtStatusFilters || showAppSubscribersFilters || showArchivedContractsFilters;

  return (
    <HorizontalForm
      sx={{
        flexWrap: 'wrap',
        rowGap: 2,
        columnGap: 2,
        overflowX: 'unset',
        alignItems: 'center',
      }}
    >
      <CustomSelect
        label="Tipo de reporte"
        size="medium"
        value={reportType}
        onChange={onReportTypeChange}
        sx={{ maxWidth: 280 }}
      >
        {reportOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 14 }}>
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>
      {showReportView && (
        <>
          <CustomSelect
            label="Vista"
            size="medium"
            value={reportView}
            onChange={onReportViewChange}
            sx={{ maxWidth: 120, mr: 0.5 }}
          >
            {GERENCIALES_DEBT_STATUS_VIEW_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomSelect>
          {showAppSubscribersFilters && (
            <CustomSelect
              label="Uso app"
              name="appUsage"
              size="medium"
              value={appSubscribersFilters.appUsage}
              onChange={onAppSubscribersFilterChange}
              sx={{ maxWidth: 160, mr: 0.5 }}
            >
              {GERENCIALES_APP_USAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </CustomSelect>
          )}
          {showArchivedContractsFilters && (
            <>
              <CustomTextField
                label="Num. Contrato"
                name="contractNumber"
                value={archivedContractsFilters.contractNumber}
                onChange={onArchivedContractsFilterChange}
                sx={{ maxWidth: 160, mr: 0.5 }}
              />
              <CustomSelect
                label="Motivo baja"
                name="releaseReason"
                size="medium"
                value={archivedContractsFilters.releaseReason}
                onChange={onArchivedContractsFilterChange}
                sx={{ maxWidth: 220, mr: 0.5 }}
                disabled={releaseReasonsLoading}
              >
                <MenuItem value="">Todos</MenuItem>
                {releaseReasons.map((reason) => (
                  <MenuItem key={reason.uuid} value={reason.uuid}>
                    {reason.description}
                  </MenuItem>
                ))}
              </CustomSelect>
            </>
          )}
        </>
      )}
      {showDebtStatusFilters && (
        <>
          <CustomSelect
            label="Poseedor"
            name="possessor"
            size="medium"
            value={debtStatusFilters.possessor}
            onChange={onDebtStatusFilterChange}
            sx={{ maxWidth: 200, mr: 0.5 }}
          >
            {GERENCIALES_DEBT_STATUS_POSSESSOR_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomSelect>
          <CustomSelect
            label="Orden"
            name="orderBy"
            size="medium"
            value={debtStatusFilters.orderBy}
            onChange={onDebtStatusFilterChange}
            sx={{ maxWidth: 220, mr: 0.5 }}
          >
            {GERENCIALES_DEBT_STATUS_ORDER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomSelect>
          <CustomTextField
            label="Num. Contrato"
            name="contractNumber"
            value={debtStatusFilters.contractNumber}
            onChange={onDebtStatusFilterChange}
            sx={{ maxWidth: 140, mr: 0.5 }}
          />
          <CustomSelect
            label="Forma de pago"
            name="paymentMethod"
            size="medium"
            value={debtStatusFilters.paymentMethod}
            onChange={onDebtStatusFilterChange}
            sx={{ maxWidth: 220, mr: 0.5 }}
            disabled={paymentMethodsLoading}
          >
            <MenuItem value="">Todas</MenuItem>
            {paymentMethods.map((method) => (
              <MenuItem key={method.uuid} value={method.uuid}>
                {method.name}
              </MenuItem>
            ))}
          </CustomSelect>

          <CustomTextField
            label="Cupones desde"
            type="number"
            name="minOverdueCoupons"
            value={debtStatusFilters.minOverdueCoupons}
            onChange={onDebtStatusFilterChange}
            inputProps={{ min: 0 }}
            sx={{ maxWidth: 140, mr: 0.5 }}
          />
          <CustomTextField
            label="Cupones hasta"
            type="number"
            name="maxOverdueCoupons"
            value={debtStatusFilters.maxOverdueCoupons}
            onChange={onDebtStatusFilterChange}
            inputProps={{ min: 0 }}
            sx={{ maxWidth: 140, mr: 0.5 }}
          />
        </>
      )}
      {showDateFilters && (
        <>
          <CustomTextField
            label="Fecha Desde"
            type="date"
            name="dateStart"
            value={dateStart}
            onChange={onDateChange}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ maxWidth: 180, mr: 0.5 }}
          />
          <CustomTextField
            label="Fecha Hasta"
            type="date"
            name="dateEnd"
            value={dateEnd}
            onChange={onDateChange}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ maxWidth: 180, mr: 0.5 }}
          />
        </>
      )}
      <CustomButton
        variant="warning"
        onClick={onReload}
        disabled={loading}
        buttonIcon={RefreshIcon}
        sx={{ mt: 1.5, mr: 0.5, pl: 4 }}
      />
      <VerticalForm withBorder={false} sx={{ mt: -3.5 }}>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </VerticalForm>
      <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
      <CustomButton
        variant="base"
        onClick={onDownloadPdf}
        disabled={loading || disableDownload}
        isLoading={pdfCooldown}
        buttonIcon={PictureAsPdfIcon}
        sx={{ mt: 1.5 }}
      >
        PDF
      </CustomButton>
      {showDownloadXlsx && (
        <CustomButton
          variant="success"
          onClick={onDownloadXlsx}
          disabled={loading || disableDownloadXlsx}
          buttonIcon={TableChartIcon}
          sx={{ mt: 1.5, ml: 1.5 }}
        >
          XLSX
        </CustomButton>
      )}
    </HorizontalForm>
  );
};

export default GerencialesFilters;
